<style lang="scss" src="./new-password-modal.scss"></style>

<template>
  <a-modal
    v-model:open="isOpen"
    class="new-password-modal"
    :closable="true"
    :mask-closable="false"
    :keyboard="false"
    :footer="null"
    @cancel="close"
    centered
    wrap-class-name="full-modal-new-password"
  >
    <template #title>
      <div class="new-password-modal__header">
        <span class="new-password-modal__title">Новый пароль</span>
        <span class="new-password-modal__description">
          Придумайте пароль из 6-10 символов.
          <br />
          Используйте цифры, заглавные и строчные буквы.
        </span>
      </div>
    </template>

    <a-form
      ref="formRef"
      class="new-password-modal__form stc-ant-field-skin"
      layout="vertical"
      :required-mark="false"
      :model="form"
      :rules="rules"
      @finish="onSubmit"
    >
      <div class="new-password-modal__field-group">
        <a-form-item
          name="password"
          label="Новый пароль"
          class="stc-ant-field-skin"
          :validate-status="showPasswordStrengthHint && !isPasswordStrong ? 'error' : undefined"
        >
          <a-input-password
            v-model:value="form.password"
            size="large"
            placeholder="Введите новый пароль"
            autocomplete="new-password"
            :icon-render="renderPasswordIcon"
          />
        </a-form-item>
        <a-alert
          v-if="showPasswordStrengthHint"
          class="new-password-modal__hint"
          :type="isPasswordStrong ? 'success' : 'error'"
          :message="passwordStrengthText"
          show-icon
        />
      </div>

      <div class="new-password-modal__field-group">
        <a-form-item
          name="confirmPassword"
          label="Подтвердить пароль"
          class="stc-ant-field-skin"
          :validate-status="showPasswordsMatchHint && !isPasswordsMatch ? 'error' : undefined"
        >
          <a-input-password
            v-model:value="form.confirmPassword"
            size="large"
            placeholder="Подтвердите пароль"
            autocomplete="new-password"
            :icon-render="renderPasswordIcon"
          />
        </a-form-item>
        <a-alert
          v-if="showPasswordsMatchHint"
          class="new-password-modal__hint"
          :type="isPasswordsMatch ? 'success' : 'error'"
          :message="passwordsMatchText"
          show-icon
        />
      </div>

      <p v-if="!resetUrlParams" class="new-password-modal__invalid-link">
        Ссылка для смены пароля недействительна или устарела. Запросите новую со страницы входа.
      </p>

      <button
        type="submit"
        class="btn btn-primary btn-medium new-password-modal__submit"
        :disabled="!canSubmit || submitLoading"
      >
        Сохранить
      </button>
    </a-form>
  </a-modal>

  <ConsultationRequestSuccessModal
    :open="passwordChangedSuccessModalOpen"
    title="Пароль изменен"
    :description="passwordChangedSuccessDescription"
    @update:open="onPasswordChangedSuccessModalOpenUpdate"
    :on-continue="handleContinue"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref, h, nextTick } from 'vue';
import {
  showStcErrorNotification,
} from '@/js/components/stc-notification';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { openLoginModal } from '@/js/widgets/login-modal';

import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

const CHANGE_PASSWORD_ENDPOINT = '/api/auth/change-password/';
const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const submitLoading = ref(false);
const passwordChangedSuccessModalOpen = ref(false);
const passwordChangedSuccessDescription = ref('');
const isWrongCheckWord = ref(false);
const formRef = ref<FormInstance>();

const PASSWORD_CHANGED_SUCCESS_FALLBACK_DESCRIPTION =
  'Вы можете авторизоваться, используя новый пароль.';
  const FAIL_PASSWORD_RECOVER_FALLBACK =
  'Ссылка не действительна. Перейдите по актуальной ссылке на почте для смены пароля.';
const form = reactive({
  password: '',
  confirmPassword: '',
});

const rules: Record<string, Rule[]> = {
  password: [{ required: true, message: 'Введите новый пароль' }],
  confirmPassword: [{ required: true, message: 'Подтвердите пароль' }],
};

const isStrongPassword = (value: string): boolean => {
  const hasLength = value.length >= 6 && value.length <= 10;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  return hasLength && hasUppercase && hasLowercase && hasDigit;
};

const isPasswordStrong = computed(() => isStrongPassword(form.password));
const isConfirmPasswordStrong = computed(() => isStrongPassword(form.confirmPassword));

const showPasswordStrengthHint = computed(() => form.password.trim().length > 0);
const showPasswordsMatchHint = computed(
  () => form.password.trim().length > 0 && form.confirmPassword.trim().length > 0,
);

const isPasswordsMatch = computed(
  () => showPasswordsMatchHint.value && form.password === form.confirmPassword,
);

const passwordStrengthText = computed(() =>
  isPasswordStrong.value ? 'Сильный пароль' : 'Слабый пароль',
);
const passwordsMatchText = computed(() =>
  isPasswordsMatch.value ? 'Пароли совпадают' : 'Пароли не совпадают',
);

type PasswordResetUrlParams = {
  userCheckword: string;
  userLogin: string;
};

const parsePasswordResetUrlParams = (): PasswordResetUrlParams | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const userCheckword = searchParams.get('USER_CHECKWORD');
  const userLogin = searchParams.get('USER_LOGIN');

  if (!userCheckword || !userLogin) return null;

  return {
    userCheckword,
    userLogin,
  };
};

const resetUrlParams = computed(() => parsePasswordResetUrlParams());

const canSubmit = computed(
  () =>
    resetUrlParams.value !== null &&
    isPasswordStrong.value &&
    isConfirmPasswordStrong.value &&
    isPasswordsMatch.value &&
    !isWrongCheckWord.value,
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key] as {code?: Number, message?: string} | string;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    else if (typeof value !== 'string' && key === 'msg' && value?.code === 401) {
      return FAIL_PASSWORD_RECOVER_FALLBACK;
    }
    else if (typeof value !== 'string' && key === 'msg' && value?.message?.trim() ) {
      return value.message.trim();
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const readChangePasswordSuccessMsg = (data: unknown): string => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const msg = (data as Record<string, unknown>).msg as {mess?: string, redirectTo?: string};
  return typeof msg?.mess === 'string' && msg?.mess.trim() ? msg?.mess.trim() : '';
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const onSubmit = async () => {
  const resetParams = resetUrlParams.value;
  if (!resetParams) {
    showStcErrorNotification('Ссылка для смены пароля недействительна или устарела');
    return;
  }

  submitLoading.value = true;
  try {
    const response = await fetch(getApiUrl(CHANGE_PASSWORD_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        login: resetParams.userLogin,
        check_word: resetParams.userCheckword,
        password: form.password,
        confirm_password: form.confirmPassword,
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      isWrongCheckWord.value = true;

      throw new Error(fromBody ?? `Ошибка смены пароля (${response.status})`);
    }

    passwordChangedSuccessDescription.value =
      readChangePasswordSuccessMsg(data) || PASSWORD_CHANGED_SUCCESS_FALLBACK_DESCRIPTION;
    trackYaGoal('register');
    close();
    passwordChangedSuccessModalOpen.value = true;
  } catch (err) {
    showStcErrorNotification(err instanceof Error ? err.message : 'Не удалось сохранить пароль');
  } finally {
    submitLoading.value = false;
  }
};

const resetFormAndHideMainModal = () => {
  form.password = '';
  form.confirmPassword = '';
  isOpen.value = false;
};

const onPasswordChangedSuccessModalOpenUpdate = (open: boolean) => {
  passwordChangedSuccessModalOpen.value = open;
  if (!open) {
    passwordChangedSuccessDescription.value = '';
    resetFormAndHideMainModal();
  }
};

const handleContinue = () => {
  nextTick(() => {
    openLoginModal();
  });
};

const open = () => {
  isOpen.value = true;
};

const close = () => {
  passwordChangedSuccessModalOpen.value = false;
  resetFormAndHideMainModal();
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

defineExpose({ open, close });
</script>

<style>
.new-password-modal__hint {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  font-family: var(--stc-font-medium);
  font-size: 12px;
  line-height: 153%;

  &.ant-alert-success {
    color: #52c41a;
  }

  &.ant-alert-error {
    color: var(--stc-red-600);
  }

  .ant-alert-icon {
    margin-inline-end: 8px;
    font-size: 14px;
  }

  .ant-alert-message {
    color: inherit;
    font: inherit;
  }
}

.new-password-modal__invalid-link {
  margin-top: 8px;
  margin-bottom: 0;
  font-family: var(--stc-font-regular);
  font-size: 12px;
  line-height: 153%;
  color: var(--stc-red-600);
}
</style>
